// src/utils/DateUtils.js

import moment from "moment";

/**
 * Utility class for handling dates and time operations
 */
class DateUtils {
  /**
   * Converts any date format to JS Date object for client-side use
   * @param {Date|Timestamp|string} dateValue - Date in any format
   * @returns {Date|null} - JS Date object or null if invalid
   */
  static toJsDate(dateValue) {
    if (!dateValue) return null;

    // Already a Date object
    if (dateValue instanceof Date) {
      return dateValue;
    }

    // Firestore Timestamp
    if (dateValue && typeof dateValue.toDate === "function") {
      return dateValue.toDate();
    }

    // Handle YYYY-MM-DD format from v-date-picker
    if (
      typeof dateValue === "string" &&
      dateValue.match(/^\d{4}-\d{2}-\d{2}$/)
    ) {
      // Use current time with the date to ensure consistent time part
      const now = new Date();
      const [year, month, day] = dateValue.split("-").map(Number);
      return new Date(
        year,
        month - 1,
        day,
        now.getHours(),
        now.getMinutes(),
        now.getSeconds()
      );
    }

    // Other string formats
    if (typeof dateValue === "string") {
      const parsed = new Date(dateValue);
      return isNaN(parsed.getTime()) ? null : parsed;
    }

    return null;
  }

  /**
   * Format date for display (e.g., "Jan 15, 2024")
   * @param {Date|Timestamp|string} dateValue - Date in any format
   * @returns {string} Formatted date or empty string if invalid
   */
  static formatDisplay(dateValue) {
    const date = this.toJsDate(dateValue);
    return date ? moment(date).format("MMM DD, YYYY") : "";
  }

  /**
   * Format date to ISO format for input fields (e.g., "2024-01-15")
   * @param {Date|Timestamp|string} dateValue - Date in any format
   * @returns {string} ISO date string or empty string if invalid
   */
  static formatISO(dateValue) {
    const date = this.toJsDate(dateValue);
    return date ? moment(date).format("YYYY-MM-DD") : "";
  }

  /**
   * Format time for display (e.g., "2:30 PM")
   * @param {Date|Timestamp|string} dateValue - Date in any format
   * @returns {string} Formatted time or empty string if invalid
   */
  static formatTime(dateValue) {
    const date = this.toJsDate(dateValue);
    return date ? moment(date).format("hh:mm A") : "";
  }

  /**
   * Format date and time for display (e.g., "Jun 06, 2025, 05:11 PM")
   * @param {Date|Timestamp|string} dateValue - Date in any format
   * @returns {string} Formatted date and time or empty string if invalid
   */
  static formatDateTime(dateValue) {
    const date = this.toJsDate(dateValue);
    return date ? moment(date).format("MMM DD, YYYY, hh:mm A") : "";
  }

  /**
   * Ensures consistent time zone handling for all dates
   * @param {Date|Timestamp|string} dateValue - Date in any format
   * @returns {Date} Date with consistent time zone
   */
  static normalizeTimeZone(dateValue) {
    const date = this.toJsDate(dateValue);
    if (!date) return null;

    // Create new date with the same values but enforcing local time zone
    return new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes(),
      date.getSeconds()
    );
  }

  /**
   * Prepares a data object for Firestore by converting JS Date objects to Firestore timestamps
   * Recursively traverses the object and converts dates at specified paths or all dates found
   *
   * @param {Object} data - The data object to prepare
   * @param {Array<string>} dateFields - Optional array of field paths to convert (if empty, all Date objects will be converted)
   * @returns {Object} - The data with dates converted to Firestore format
   */
  static prepareForFirestore(data, dateFields = []) {
    if (!data) return data;

    // Clone the data to avoid modifying the original
    const clonedData = JSON.parse(JSON.stringify(data));

    // If we have a list of fields, process just those
    if (dateFields && dateFields.length > 0) {
      dateFields.forEach(fieldPath => {
        const pathParts = fieldPath.split(".");
        let current = clonedData;

        // Navigate to the nested property
        for (let i = 0; i < pathParts.length - 1; i++) {
          if (!current[pathParts[i]]) return;
          current = current[pathParts[i]];
        }

        // Convert the date at the specified path
        const lastPart = pathParts[pathParts.length - 1];
        if (current[lastPart] && current[lastPart] instanceof Date) {
          current[lastPart] = new Date(current[lastPart]);
        } else if (typeof current[lastPart] === "string") {
          // Try to convert string dates
          const possibleDate = new Date(current[lastPart]);
          if (!isNaN(possibleDate.getTime())) {
            current[lastPart] = possibleDate;
          }
        }
      });

      return clonedData;
    }

    // Otherwise recursively process all properties
    return this._processObjectDates(clonedData);
  }

  /**
   * Recursively process an object to convert all Date objects
   * Private helper method
   */
  static _processObjectDates(obj) {
    if (!obj || typeof obj !== "object") return obj;

    // Handle arrays
    if (Array.isArray(obj)) {
      return obj.map(item => this._processObjectDates(item));
    }

    // Process object properties
    const result = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const value = obj[key];

        if (value instanceof Date) {
          // Convert Date to Firestore timestamp format
          result[key] = new Date(value);
        } else if (typeof value === "string") {
          // Try to detect and convert string dates
          const possibleDate = new Date(value);
          if (
            !isNaN(possibleDate.getTime()) &&
            (value.includes("T") ||
              value.includes("-") ||
              value.includes("/")) &&
            value.length > 8
          ) {
            result[key] = possibleDate;
          } else {
            result[key] = value;
          }
        } else if (value && typeof value === "object") {
          // Recursively process nested objects
          result[key] = this._processObjectDates(value);
        } else {
          result[key] = value;
        }
      }
    }

    return result;
  }

  /**
   * Format a date to a standard display format
   * @param {Date|string} date - The date to format
   * @param {string} format - The format type ('short', 'medium', 'long', or custom)
   * @returns {string} - The formatted date string
   */
  static formatDate(date, format = "medium") {
    if (!date) return "";

    const d = typeof date === "string" ? new Date(date) : date;
    if (isNaN(d.getTime())) return "";

    // Standard format options
    switch (format) {
      case "short":
        return d.toLocaleDateString();
      case "medium":
        return d.toLocaleDateString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric"
        });
      case "long":
        return d.toLocaleDateString(undefined, {
          year: "numeric",
          month: "long",
          day: "numeric",
          weekday: "long"
        });
      default:
        return d.toLocaleDateString();
    }
  }
}

export default DateUtils;
